/**
 * Module dependencies.
 */

import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { BtcCycle } from 'app/hooks/requests/crypto-bot/use-crypto-bot';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * `Props` type.
 */

type Props = {
  cycle: BtcCycle;
};

/**
 * Compact USD price for the axis/tooltip.
 */

function usd(value: number): string {
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(value >= 100000 ? 0 : 1)}k`;
  }

  return `$${value.toFixed(0)}`;
}

/**
 * Year label for the time axis.
 */

function year(ts: number): string {
  return new Date(ts).getUTCFullYear().toString();
}

/**
 * Tooltip.
 */

function CycleTooltip({ active, payload }: any): JSX.Element | null {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload;

  if (!point) {
    return null;
  }

  return (
    <div className={'rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-md'}>
      <div className={'mb-1 font-medium'}>{new Date(point.t).toISOString().slice(0, 10)}</div>

      {point.price != null && (
        <div className={'flex items-center justify-between gap-4'}>
          <span className={'text-muted-foreground'}>{'Price'}</span>
          <span className={'font-semibold tabular-nums'}>{usd(point.price)}</span>
        </div>
      )}

      <div className={'flex items-center justify-between gap-4'}>
        <span className={'text-muted-foreground'}>{point.price == null ? 'Projected fair' : 'Fair value'}</span>
        <span className={'tabular-nums'}>{usd(point.fair)}</span>
      </div>
    </div>
  );
}

/**
 * Export `CryptoBotCycleChart` component.
 *
 * BTC price (log scale) with the log-regression fair-value + bands, the
 * 200-week MA, halving markers, and shaded bull/bear windows from the halving
 * cycle. A dashed line marks where we are now.
 */

export function CryptoBotCycleChart({ cycle }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { series, phases, halvings, current } = cycle;

  return (
    <ResponsiveContainer height={340} width={'100%'}>
      <ComposedChart data={series} margin={{ bottom: 0, left: 0, right: 8, top: 8 }}>
        <CartesianGrid stroke={'hsl(var(--border))'} strokeDasharray={'3 3'} vertical={false} />

        {/* Bull/bear windows from the halving cycle. */}
        {phases.map((phase, index) => (
          <ReferenceArea
            fill={phase.type === 'bull' ? '#16a34a' : '#dc2626'}
            fillOpacity={0.06}
            ifOverflow={'hidden'}
            key={`phase-${index}`}
            x1={phase.start}
            x2={phase.end}
          />
        ))}

        {/* Halving markers. */}
        {halvings.map(halving => (
          <ReferenceLine
            ifOverflow={'hidden'}
            key={`halving-${halving.t}`}
            label={{
              fill: 'hsl(var(--muted-foreground))',
              fontSize: 10,
              position: 'insideTopRight',
              value: `⛏ ${halving.label}`
            }}
            stroke={'hsl(var(--muted-foreground))'}
            strokeDasharray={'2 2'}
            x={halving.t}
          />
        ))}

        {/* You are here. */}
        <ReferenceLine
          ifOverflow={'extendDomain'}
          label={{
            fill: 'hsl(var(--primary))',
            fontSize: 10,
            position: 'insideTopLeft',
            value: translate('cryptoBot.overview.cycle.youAreHere')
          }}
          stroke={'hsl(var(--primary))'}
          strokeWidth={1.5}
          x={current.t}
        />

        <XAxis
          axisLine={false}
          dataKey={'t'}
          domain={['dataMin', 'dataMax']}
          scale={'time'}
          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
          tickFormatter={year}
          tickLine={false}
          type={'number'}
        />

        <YAxis
          axisLine={false}
          domain={['auto', 'auto']}
          scale={'log'}
          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
          tickFormatter={usd}
          tickLine={false}
          width={48}
        />

        <Tooltip content={<CycleTooltip />} />

        {/* Regression bands + fair value. */}
        <Line
          dataKey={'upper'}
          dot={false}
          isAnimationActive={false}
          stroke={'#dc2626'}
          strokeDasharray={'3 3'}
          strokeOpacity={0.5}
          strokeWidth={1}
          type={'monotone'}
        />
        <Line
          dataKey={'lower'}
          dot={false}
          isAnimationActive={false}
          stroke={'#16a34a'}
          strokeDasharray={'3 3'}
          strokeOpacity={0.5}
          strokeWidth={1}
          type={'monotone'}
        />
        <Line
          dataKey={'fair'}
          dot={false}
          isAnimationActive={false}
          stroke={'#d97706'}
          strokeWidth={1.5}
          type={'monotone'}
        />

        {/* 200-week MA. */}
        <Line
          connectNulls
          dataKey={'ma200w'}
          dot={false}
          isAnimationActive={false}
          stroke={'#2563eb'}
          strokeDasharray={'1 2'}
          strokeWidth={1.5}
          type={'monotone'}
        />

        {/* Price. */}
        <Line
          dataKey={'price'}
          dot={false}
          isAnimationActive={false}
          stroke={'hsl(var(--foreground))'}
          strokeWidth={2}
          type={'monotone'}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
